(function () {
  'use strict';

  if (window.SickHausUniversalProductCards) {
    window.SickHausUniversalProductCards.init(document);
    return;
  }

  var ROOT_SELECTOR = '[data-sh-universal-grid]';
  var rootStates = new WeakMap();
  var pendingRoots = new WeakSet();
  var rootObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      rootObserver.unobserve(entry.target);
      pendingRoots.delete(entry.target);
      initRoot(entry.target);
    });
  }, { rootMargin: '0px', threshold: 0.01 }) : null;

  function queueRoot(root) {
    if (!root || rootStates.has(root) || pendingRoots.has(root)) return;
    var designMode = Boolean(window.Shopify && window.Shopify.designMode);
    var rect = root.getBoundingClientRect();
    var nearViewport = rect.top < (window.innerHeight || document.documentElement.clientHeight || 800) * 1.05;
    if (designMode || !rootObserver || nearViewport) {
      initRoot(root);
      return;
    }
    pendingRoots.add(root);
    rootObserver.observe(root);
  }

  function init(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) queueRoot(target);
    if (target.querySelectorAll) target.querySelectorAll(ROOT_SELECTOR).forEach(queueRoot);
  }

  function initRoot(root) {
    if (!root || rootStates.has(root)) return;

    var state = {
      root: root,
      style: root.dataset.variantSelectorStyle === 'quiet-count' ? 'quiet-count' : 'adaptive',
      quickAddEnabled: root.dataset.quickAddEnabled !== 'false',
      cartAddUrl: root.dataset.cartAddUrl || '/cart/add.js',
      activeCard: null,
      mode: null,
      selectedOptions: [],
      lastFocus: null,
      backdrop: null,
      drawer: null,
      observer: null,
      clickHandler: null,
      keyHandler: null,
      secondaryHandler: null,
      secondaryFocusHandler: null
    };

    rootStates.set(root, state);
    root.dataset.shUniversalReady = 'true';
    enhanceCards(state);

    state.clickHandler = function (event) { onRootClick(state, event); };
    state.keyHandler = function (event) { onKeydown(state, event); };
    root.addEventListener('click', state.clickHandler);
    document.addEventListener('keydown', state.keyHandler);

    // Desktop hover imagery is opt-in on first pointer/focus intent. Mobile
    // never downloads secondary hover images it cannot display.
    state.secondaryHandler = function (event) {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      var card = event.target.closest && event.target.closest('[data-sh-uc-card]');
      if (card && state.root.contains(card)) hydrateSecondaryImage(card);
    };
    state.secondaryFocusHandler = state.secondaryHandler;
    root.addEventListener('pointerover', state.secondaryHandler, { passive: true });
    root.addEventListener('focusin', state.secondaryFocusHandler);

    state.observer = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (mutation) {
        var target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
        return target && !target.closest('.sh-uc-drawer') && !!target.closest('[data-sh-uc-card], ' + ROOT_SELECTOR);
      });
      if (relevant) window.requestAnimationFrame(function () { enhanceCards(state); });
    });
    state.observer.observe(root, { childList: true, subtree: true });
  }

  function enhanceCards(state) {
    state.root.querySelectorAll('[data-sh-uc-card]').forEach(function (card) {
      if (card.dataset.shUcEnhanced === 'true') {
        syncControl(state, card);
        return;
      }
      card.dataset.shUcEnhanced = 'true';
      buildSelector(state, card);
      initCardMedia(card);
    });
  }

  function cardMediaImages(card) {
    return card ? Array.prototype.slice.call(card.querySelectorAll('[data-sh-uc-media-image]')) : [];
  }

  function hydrateCardMedia(image) {
    if (!image || image.dataset.shUcHydrated === 'true') return;
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    if (image.dataset.sizes) image.sizes = image.dataset.sizes;
    if (image.dataset.src) image.src = image.dataset.src;
    image.loading = 'lazy';
    image.dataset.shUcHydrated = 'true';
  }

  function setCardMediaIndex(card, index, manual) {
    var images = cardMediaImages(card);
    if (!images.length) return;
    index = Math.max(0, Math.min(images.length - 1, Number(index) || 0));
    if (images[index]) hydrateCardMedia(images[index]);
    card.dataset.shUcMediaIndex = String(index);
    card.classList.toggle('is-media-controlled', Boolean(manual));
    images.forEach(function (image, imageIndex) {
      image.classList.toggle('is-selected-media', imageIndex === index);
    });
    card.querySelectorAll('[data-sh-uc-media-choice]').forEach(function (button) {
      var active = Number(button.dataset.shUcMediaChoice) === index;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    if (manual) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'product_card_media_select', product_handle: card.dataset.productHandle || '', media_index: index + 1, device_class: window.innerWidth <= 749 ? 'mobile' : 'desktop' });
      if (typeof window.clarity === 'function') window.clarity('event', 'product_card_media_select');
    }
  }

  function initCardMedia(card) {
    if (!card || card.dataset.shUcMediaReady === 'true') return;
    var images = cardMediaImages(card);
    if (images.length < 2) return;
    card.dataset.shUcMediaReady = 'true';
    setCardMediaIndex(card, 0, false);
  }

  function getChoices(card) {
    return Array.prototype.slice.call(card.querySelectorAll('[data-sh-uc-variant]')).map(function (button, index) {
      return {
        index: index,
        button: button,
        label: button.dataset.label || ('Option ' + (index + 1)),
        swatch: button.dataset.swatch || '',
        isColour: Boolean(button.dataset.swatch),
        variantId: String(button.dataset.variantId || ''),
        url: button.dataset.url || '#',
        image: button.dataset.image || '',
        imageAlt: button.dataset.imageAlt || '',
        price: button.dataset.price || '',
        comparePrice: button.dataset.comparePrice || '',
        priceRaw: Number(button.dataset.priceRaw || 0),
        comparePriceRaw: Number(button.dataset.comparePriceRaw || 0),
        unavailable: button.disabled,
        active: button.classList.contains('is-active')
      };
    });
  }

  function buildSelector(state, card) {
    var source = card.querySelector('[data-sh-uc-source]');
    if (!source) return;
    var choices = getChoices(card);
    if (choices.length < 2) {
      source.hidden = true;
      return;
    }

    source.hidden = false;
    var control = document.createElement('div');
    control.className = 'sh-uc-selector';

    /* Only use Curated Three when every choice has a genuine visual swatch.
       Named finishes and other visual options without a trustworthy swatch
       fall back to the quiet active-label treatment rather than abbreviated
       text pills. */
    var useQuietControl = state.style === 'quiet-count' || !choices.every(function (choice) {
      return choice.isColour;
    });
    control.dataset.shUcControl = useQuietControl ? 'quiet-count' : 'adaptive';

    if (useQuietControl) {
      control.innerHTML = '<button class="sh-uc-selector__quiet" type="button" data-sh-uc-open-palette aria-haspopup="dialog"><span class="sh-uc-selector__quiet-dot" aria-hidden="true"></span><span class="sh-uc-selector__quiet-label"></span><span class="sh-uc-selector__quiet-count"></span></button>';
    } else {
      var row = document.createElement('div');
      row.className = 'sh-uc-selector__row';
      choices.slice(0, 3).forEach(function (choice) { row.appendChild(createChoiceButton(choice)); });

      if (choices.length === 4) {
        var fourth = document.createElement('button');
        fourth.type = 'button';
        fourth.className = 'sh-uc-selector__more';
        fourth.dataset.shUcChoice = '3';
        fourth.textContent = '+1';
        fourth.title = choices[3].label;
        fourth.setAttribute('aria-label', 'Preview ' + choices[3].label);
        row.appendChild(fourth);
      } else if (choices.length >= 5) {
        var more = document.createElement('button');
        more.type = 'button';
        more.className = 'sh-uc-selector__more';
        more.dataset.shUcOpenPalette = '';
        more.textContent = '+' + (choices.length - 3);
        more.title = 'View all ' + choices.length + ' options';
        more.setAttribute('aria-label', 'View all ' + choices.length + ' options');
        more.setAttribute('aria-haspopup', 'dialog');
        row.appendChild(more);
      }
      control.appendChild(row);
    }

    source.appendChild(control);
    syncControl(state, card);
  }

  function createChoiceButton(choice) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'sh-uc-selector__choice' + (choice.isColour ? '' : ' sh-uc-selector__choice--text');
    button.dataset.shUcChoice = String(choice.index);
    if (choice.isColour) button.style.setProperty('--sh-uc-swatch', choice.swatch);
    else button.textContent = shortLabel(choice.label);
    button.title = choice.label;
    button.setAttribute('aria-label', 'Preview ' + choice.label);
    if (choice.unavailable) {
      button.disabled = true;
      button.classList.add('is-unavailable');
    }
    return button;
  }

  function shortLabel(label) {
    var text = String(label || '').trim();
    if (text.length <= 10) return text;
    var words = text.split(/\s*\/\s*|\s+-\s+|\s+/).filter(Boolean);
    if (words.length > 1) return words.map(function (word) { return word.charAt(0); }).join('').slice(0, 5).toUpperCase();
    return text.slice(0, 9) + '…';
  }

  function syncControl(state, card) {
    var control = card.querySelector('[data-sh-uc-control]');
    if (!control) return;
    var choices = getChoices(card);
    if (!choices.length) return;
    var activeIndex = choices.findIndex(function (choice) { return choice.active; });
    if (activeIndex < 0) activeIndex = 0;
    var active = choices[activeIndex];

    control.querySelectorAll('[data-sh-uc-choice]').forEach(function (button) {
      var index = Number(button.dataset.shUcChoice);
      button.classList.toggle('is-active', index === activeIndex);
      button.setAttribute('aria-pressed', index === activeIndex ? 'true' : 'false');
    });

    var more = control.querySelector('[data-sh-uc-open-palette].sh-uc-selector__more');
    if (more) more.classList.toggle('is-active', activeIndex >= 3);

    var quiet = control.querySelector('.sh-uc-selector__quiet');
    if (quiet) {
      quiet.classList.toggle('is-text', !active.isColour);
      if (active.isColour) quiet.style.setProperty('--sh-uc-swatch', active.swatch);
      else quiet.style.removeProperty('--sh-uc-swatch');
      quiet.querySelector('.sh-uc-selector__quiet-label').textContent = active.label;
      var noun = choiceNoun(card, choices);
      quiet.querySelector('.sh-uc-selector__quiet-count').textContent = choices.length + ' ' + noun;
      quiet.setAttribute('aria-label', active.label + '. View all ' + choices.length + ' ' + noun);
    }
  }

  function choiceNoun(card, choices) {
    var source = card && card.querySelector('[data-sh-uc-source]');
    var optionName = String(source && source.dataset.optionName || '').toLowerCase();
    if (optionName.indexOf('colour') !== -1 || optionName.indexOf('color') !== -1 || optionName.indexOf('colourway') !== -1) return 'colours';
    if (optionName.indexOf('upholstery') !== -1 || optionName.indexOf('fabric') !== -1 || optionName.indexOf('material') !== -1 || optionName.indexOf('finish') !== -1) return 'finishes';
    return choices && choices.every(function (choice) { return choice.isColour; }) ? 'colours' : 'finishes';
  }

  function onRootClick(state, event) {
    var mediaChoice = event.target.closest('[data-sh-uc-media-choice]');
    if (mediaChoice && state.root.contains(mediaChoice)) {
      event.preventDefault();
      event.stopPropagation();
      var mediaCard = mediaChoice.closest('[data-sh-uc-card]');
      if (mediaCard) setCardMediaIndex(mediaCard, Number(mediaChoice.dataset.shUcMediaChoice), true);
      return;
    }

    var viewProduct = event.target.closest('[data-sh-uc-view-product]');
    if (viewProduct && state.drawer && state.drawer.contains(viewProduct)) {
      event.preventDefault();
      event.stopPropagation();
      var destination = viewProduct.getAttribute('href');
      if (destination && destination !== '#') window.location.assign(destination);
      return;
    }

    var choice = event.target.closest('[data-sh-uc-choice]');
    if (choice && state.root.contains(choice)) {
      event.preventDefault();
      event.stopPropagation();
      var choiceCard = choice.closest('[data-sh-uc-card]');
      if (choiceCard) selectChoice(state, choiceCard, Number(choice.dataset.shUcChoice));
      return;
    }

    var paletteTrigger = event.target.closest('[data-sh-uc-open-palette]');
    if (paletteTrigger && state.root.contains(paletteTrigger)) {
      event.preventDefault();
      event.stopPropagation();
      var paletteCard = paletteTrigger.closest('[data-sh-uc-card]');
      if (paletteCard) openDrawer(state, paletteCard, 'palette', paletteTrigger);
      return;
    }

    var paletteChoice = event.target.closest('[data-sh-uc-palette-choice]');
    if (paletteChoice && state.drawer && state.drawer.contains(paletteChoice)) {
      event.preventDefault();
      if (state.activeCard) {
        selectChoice(state, state.activeCard, Number(paletteChoice.dataset.shUcPaletteChoice));
        renderDrawer(state);
      }
      return;
    }

    var quick = event.target.closest('[data-sh-uc-quick]');
    if (quick && state.root.contains(quick)) {
      event.preventDefault();
      event.stopPropagation();
      var quickCard = quick.closest('[data-sh-uc-card]');
      if (!quickCard) return;
      var product = getProductData(quickCard);
      if (product && product.variants.filter(function (variant) { return variant.available; }).length === 1) {
        addVariant(state, quickCard, String(product.variants.find(function (variant) { return variant.available; }).id), quick);
      } else {
        openDrawer(state, quickCard, 'quick', quick);
      }
      return;
    }

    var optionButton = event.target.closest('[data-sh-uc-option-index]');
    if (optionButton && state.drawer && state.drawer.contains(optionButton)) {
      event.preventDefault();
      var optionIndex = Number(optionButton.dataset.shUcOptionIndex);
      state.selectedOptions[optionIndex] = optionButton.dataset.value;
      var matching = findVariant(getProductData(state.activeCard), state.selectedOptions);
      if (matching) applyVariantToCard(state, state.activeCard, matching);
      renderDrawer(state);
      return;
    }

    var addButton = event.target.closest('[data-sh-uc-add]');
    if (addButton && state.drawer && state.drawer.contains(addButton)) {
      event.preventDefault();
      var selected = findVariant(getProductData(state.activeCard), state.selectedOptions);
      if (selected && selected.available) addVariant(state, state.activeCard, String(selected.id), addButton, true);
      return;
    }

    if (event.target.closest('[data-sh-uc-close]') || event.target === state.backdrop) {
      event.preventDefault();
      closeDrawer(state);
    }
  }

  function selectChoice(state, card, index) {
    var choices = getChoices(card);
    var selected = choices[index];
    if (!selected || selected.unavailable) return;

    choices.forEach(function (choice) { choice.button.classList.toggle('is-active', choice.index === index); });
    applyChoiceToCard(card, selected);
    syncControl(state, card);
  }

  function applyChoiceToCard(card, choice) {
    card.dataset.selectedVariantId = choice.variantId;
    card.querySelectorAll('[data-sh-uc-product-link]').forEach(function (link) { link.href = choice.url; });
    var label = card.querySelector('[data-sh-uc-option-label]');
    if (label) label.textContent = choice.label;
    var quick = card.querySelector('[data-sh-uc-quick]');
    if (quick) quick.dataset.selectedVariantId = choice.variantId;
    updatePrice(card, choice.price, choice.comparePrice);
    updateSaleBadge(card, choice.comparePriceRaw > choice.priceRaw);
    if (choice.image) updateCardImage(card, choice.image, choice.imageAlt);
  }

  function applyVariantToCard(state, card, variant) {
    card.dataset.selectedVariantId = String(variant.id);
    var product = getProductData(card);
    var url = product.url + '?variant=' + encodeURIComponent(variant.id);
    card.querySelectorAll('[data-sh-uc-product-link]').forEach(function (link) { link.href = url; });
    var quick = card.querySelector('[data-sh-uc-quick]');
    if (quick) quick.dataset.selectedVariantId = String(variant.id);
    updatePrice(card, variant.priceFormatted, variant.compareAtFormatted || '');
    updateSaleBadge(card, Number(variant.compareAt || 0) > Number(variant.price || 0));
    if (variant.image) updateCardImage(card, variant.image, variant.imageAlt || product.title);

    if (product.swatchOptionIndex >= 0) {
      var value = variant.options[product.swatchOptionIndex];
      var choices = getChoices(card);
      choices.forEach(function (choice) {
        var active = choice.label === value;
        choice.button.classList.toggle('is-active', active);
        if (active) {
          var label = card.querySelector('[data-sh-uc-option-label]');
          if (label) label.textContent = value;
        }
      });
      syncControl(state, card);
    }
  }


  function hydrateSecondaryImage(card) {
    if (!card || card.dataset.shSecondaryReady === 'true') return;
    var image = card.querySelector('[data-sh-uc-secondary-image]');
    if (!image) return;
    var source = image.dataset.src;
    var sourceSet = image.dataset.srcset;
    var sizes = image.dataset.sizes;
    if (sourceSet) image.srcset = sourceSet;
    if (sizes) image.sizes = sizes;
    if (source) image.src = source;
    image.loading = 'lazy';
    card.dataset.shSecondaryReady = 'true';
  }

  function updateSaleBadge(card, onSale) {
    var badge = card.querySelector('[data-sh-uc-badge]');
    if (!badge || badge.dataset.static === 'true') return;
    if (badge.dataset.allowSale !== 'true') {
      badge.hidden = true;
      badge.textContent = '';
      return;
    }
    badge.hidden = !onSale;
    badge.textContent = onSale ? 'Sale' : '';
  }

  function updatePrice(card, price, comparePrice) {
    var node = card.querySelector('[data-sh-uc-price]');
    if (!node) return;
    node.innerHTML = comparePrice ? '<s>' + escapeHtml(comparePrice) + '</s><span>' + escapeHtml(price) + '</span>' : '<span>' + escapeHtml(price) + '</span>';
  }

  function updateCardImage(card, source, alt) {
    var primary = card.querySelector('[data-sh-uc-primary-image]');
    if (!primary) return;
    card.classList.add('is-changing');
    preload(source).then(function () {
      primary.removeAttribute('srcset');
      primary.src = source;
      primary.alt = alt || primary.alt;
      setCardMediaIndex(card, 0, false);
      /* Preserve the card's distinct secondary and detail images so discovery
         remains useful after a customer previews a variant. */
      window.requestAnimationFrame(function () { card.classList.remove('is-changing'); });
    }).catch(function () { card.classList.remove('is-changing'); });
  }

  function preload(source) {
    return new Promise(function (resolve, reject) {
      var image = new Image();
      image.onload = resolve;
      image.onerror = reject;
      image.src = source;
    });
  }

  function getProductData(card) {
    if (!card) return null;
    if (card._shUcProduct) return card._shUcProduct;
    var script = card.querySelector('[data-sh-uc-product-json]');
    if (!script) return null;
    try {
      card._shUcProduct = JSON.parse(script.textContent);
      return card._shUcProduct;
    } catch (error) {
      console.error('SickHaus product card data could not be parsed.', error);
      return null;
    }
  }

  function createDrawer(state) {
    var backdrop = document.createElement('div');
    backdrop.className = 'sh-uc-drawer__backdrop';
    backdrop.dataset.shUcClose = '';
    backdrop.setAttribute('aria-hidden', 'true');

    var drawer = document.createElement('aside');
    drawer.className = 'sh-uc-drawer';
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('aria-label', 'Choose product options');
    drawer.innerHTML = [
      '<div class="sh-uc-drawer__head">',
        '<div><small data-sh-uc-drawer-eyebrow>Colour &amp; finish</small><strong data-sh-uc-drawer-heading>Choose your version</strong></div>',
        '<button class="sh-uc-drawer__close" type="button" data-sh-uc-close aria-label="Close">×</button>',
      '</div>',
      '<div class="sh-uc-drawer__scroll">',
        '<div class="sh-uc-drawer__media"><img data-sh-uc-drawer-image alt=""></div>',
        '<div class="sh-uc-drawer__body">',
          '<p class="sh-uc-drawer__kicker">Available options</p>',
          '<h2 data-sh-uc-drawer-title></h2>',
          '<div class="sh-uc-drawer__price" data-sh-uc-drawer-price></div>',
          '<div class="sh-uc-drawer__selection"><span>Selected</span><strong data-sh-uc-drawer-selection></strong></div>',
          '<div class="sh-uc-drawer__palette" data-sh-uc-palette></div>',
          '<div class="sh-uc-drawer__options" data-sh-uc-options></div>',
          '<p class="sh-uc-drawer__status" data-sh-uc-status aria-live="polite"></p>',
        '</div>',
      '</div>',
      '<div class="sh-uc-drawer__foot">',
        '<button class="sh-uc-drawer__primary" type="button" data-sh-uc-add hidden>Add to bag</button>',
        '<a class="sh-uc-drawer__link" data-sh-uc-view-product href="#" hidden>View product</a>',
      '</div>'
    ].join('');

    state.root.appendChild(backdrop);
    state.root.appendChild(drawer);
    state.backdrop = backdrop;
    state.drawer = drawer;
  }

  function openDrawer(state, card, mode, trigger) {
    if (!state.drawer) createDrawer(state);
    state.activeCard = card;
    state.mode = mode;
    state.lastFocus = trigger || document.activeElement;
    seedSelectedOptions(state);
    renderDrawer(state);
    state.backdrop.classList.add('is-open');
    state.drawer.classList.add('is-open');
    state.backdrop.setAttribute('aria-hidden', 'false');
    state.drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('sh-uc-lock');
    var close = state.drawer.querySelector('[data-sh-uc-close]');
    if (close) close.focus();
  }

  function seedSelectedOptions(state) {
    var product = getProductData(state.activeCard);
    if (!product) return;
    var selectedId = String(state.activeCard.dataset.selectedVariantId || product.initialVariantId || '');
    var selected = product.variants.find(function (variant) { return String(variant.id) === selectedId; }) || product.variants[0];
    state.selectedOptions = selected ? selected.options.slice() : [];
  }

  function renderDrawer(state) {
    var card = state.activeCard;
    var product = getProductData(card);
    if (!card || !product) return;

    var selectedVariant = findVariant(product, state.selectedOptions) || product.variants.find(function (variant) { return String(variant.id) === String(card.dataset.selectedVariantId); }) || product.variants[0];
    var choices = getChoices(card);
    var activeChoiceIndex = choices.findIndex(function (choice) { return choice.active; });
    if (activeChoiceIndex < 0) activeChoiceIndex = 0;
    var activeChoice = choices[activeChoiceIndex];

    setText(state.drawer, '[data-sh-uc-drawer-title]', product.title);
    setText(state.drawer, '[data-sh-uc-drawer-eyebrow]', state.mode === 'quick' ? 'Quick add' : 'Colour & finish');
    setText(state.drawer, '[data-sh-uc-drawer-heading]', state.mode === 'quick' ? 'Choose your options' : 'Choose your version');

    var image = state.drawer.querySelector('[data-sh-uc-drawer-image]');
    var cardImage = card.querySelector('[data-sh-uc-primary-image]');
    if (image) {
      image.src = selectedVariant && selectedVariant.image ? selectedVariant.image : (cardImage ? cardImage.currentSrc || cardImage.src : '');
      image.alt = product.title;
    }

    var price = state.drawer.querySelector('[data-sh-uc-drawer-price]');
    if (price && selectedVariant) price.innerHTML = selectedVariant.compareAtFormatted ? '<s>' + escapeHtml(selectedVariant.compareAtFormatted) + '</s><span>' + escapeHtml(selectedVariant.priceFormatted) + '</span>' : '<span>' + escapeHtml(selectedVariant.priceFormatted) + '</span>';

    var selection = state.drawer.querySelector('[data-sh-uc-drawer-selection]');
    if (selection) selection.textContent = state.mode === 'quick' ? state.selectedOptions.join(' · ') : (activeChoice ? activeChoice.label : '');

    var palette = state.drawer.querySelector('[data-sh-uc-palette]');
    var options = state.drawer.querySelector('[data-sh-uc-options]');
    var status = state.drawer.querySelector('[data-sh-uc-status]');
    var addButton = state.drawer.querySelector('[data-sh-uc-add]');
    var viewLink = state.drawer.querySelector('[data-sh-uc-view-product]');
    var selectedProductUrl = product.url;
    if (selectedVariant && selectedVariant.id) selectedProductUrl += '?variant=' + encodeURIComponent(selectedVariant.id);
    if (viewLink) viewLink.href = selectedProductUrl;

    if (state.mode === 'palette') {
      palette.hidden = false;
      options.hidden = true;
      palette.innerHTML = choices.map(function (choice, index) {
        var visual = choice.isColour
          ? '<span class="sh-uc-drawer__palette-dot" style="--sh-uc-swatch:' + escapeAttr(choice.swatch) + '" aria-hidden="true"></span>'
          : '<span class="sh-uc-drawer__palette-dot is-text" aria-hidden="true">' + escapeHtml(shortLabel(choice.label)) + '</span>';
        return '<button class="sh-uc-drawer__palette-choice' + (index === activeChoiceIndex ? ' is-active' : '') + '" type="button" data-sh-uc-palette-choice="' + index + '"' + (choice.unavailable ? ' disabled' : '') + '>' + visual + '<span>' + escapeHtml(choice.label) + '</span></button>';
      }).join('');
      status.textContent = selectedVariant && selectedVariant.available ? 'Ready when you are.' : 'This option is currently unavailable.';
      addButton.hidden = !state.quickAddEnabled;
      addButton.disabled = !selectedVariant || !selectedVariant.available;
      addButton.textContent = selectedVariant && selectedVariant.available ? 'Add to bag' : 'Currently unavailable';
      viewLink.hidden = false;
    } else {
      palette.hidden = true;
      options.hidden = false;
      options.innerHTML = product.options.map(function (option, optionIndex) {
        var buttons = option.values.map(function (value) {
          var active = state.selectedOptions[optionIndex] === value;
          var available = optionValueAvailable(product, optionIndex, value, state.selectedOptions);
          return '<button class="sh-uc-drawer__option' + (active ? ' is-active' : '') + '" type="button" data-sh-uc-option-index="' + optionIndex + '" data-value="' + escapeAttr(value) + '"' + (available ? '' : ' disabled') + ' aria-pressed="' + (active ? 'true' : 'false') + '">' + escapeHtml(value) + '</button>';
        }).join('');
        return '<fieldset class="sh-uc-drawer__option-group"><legend>' + escapeHtml(option.name) + '</legend><div class="sh-uc-drawer__option-values">' + buttons + '</div></fieldset>';
      }).join('');
      viewLink.hidden = false;
      addButton.hidden = false;
      addButton.disabled = !selectedVariant || !selectedVariant.available;
      addButton.textContent = selectedVariant && selectedVariant.available ? 'Add to bag' : 'Currently unavailable';
      status.textContent = selectedVariant && selectedVariant.available ? 'Ready when you are.' : 'This combination is currently unavailable.';
    }
  }

  function optionValueAvailable(product, optionIndex, value, selectedOptions) {
    return product.variants.some(function (variant) {
      if (!variant.available || variant.options[optionIndex] !== value) return false;
      return variant.options.every(function (variantValue, index) {
        return index === optionIndex || !selectedOptions[index] || variantValue === selectedOptions[index];
      });
    });
  }

  function findVariant(product, selectedOptions) {
    if (!product) return null;
    return product.variants.find(function (variant) {
      return variant.options.every(function (value, index) { return value === selectedOptions[index]; });
    }) || null;
  }

  function addVariant(state, card, variantId, button, closeAfter) {
    if (!variantId || !button || button.disabled) return;
    var labelNode = button.querySelector('span');
    var original = labelNode ? labelNode.textContent : button.textContent;
    button.disabled = true;
    button.classList.add('is-loading');
    setActionText(button, 'Adding…');

    fetch(state.cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 })
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) throw new Error(data && (data.description || data.message) || 'Unable to add item');
        return data;
      });
    }).then(function (data) {
      setActionText(button, 'Added ✓');
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));
      if (closeAfter) window.setTimeout(function () { closeDrawer(state); }, 650);
    }).catch(function (error) {
      console.error('SickHaus quick add failed:', error);
      setActionText(button, 'Try again');
    }).finally(function () {
      window.setTimeout(function () {
        if (!button.isConnected) return;
        button.disabled = false;
        button.classList.remove('is-loading');
        setActionText(button, original);
      }, 1800);
    });
  }

  function setActionText(button, value) {
    var span = button.querySelector('span');
    if (span) span.textContent = value;
    else button.textContent = value;
  }

  function closeDrawer(state) {
    if (!state.drawer || !state.drawer.classList.contains('is-open')) return;
    state.backdrop.classList.remove('is-open');
    state.drawer.classList.remove('is-open');
    state.backdrop.setAttribute('aria-hidden', 'true');
    state.drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('sh-uc-lock');
    var focus = state.lastFocus;
    state.activeCard = null;
    state.mode = null;
    state.lastFocus = null;
    if (focus && typeof focus.focus === 'function') window.setTimeout(function () { focus.focus(); }, 30);
  }

  function onKeydown(state, event) {
    if (!state.drawer || !state.drawer.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer(state);
      return;
    }
    if (event.key !== 'Tab') return;
    var focusable = Array.prototype.slice.call(state.drawer.querySelectorAll('button:not([disabled]):not([hidden]),a[href]:not([hidden])')).filter(function (node) { return node.offsetParent !== null; });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }


  function destroyRoot(root) {
    if (pendingRoots.has(root) && rootObserver) {
      rootObserver.unobserve(root);
      pendingRoots.delete(root);
    }
    var state = rootStates.get(root);
    if (!state) return;
    if (state.observer) state.observer.disconnect();
    if (state.clickHandler) root.removeEventListener('click', state.clickHandler);
    if (state.keyHandler) document.removeEventListener('keydown', state.keyHandler);
    if (state.secondaryHandler) root.removeEventListener('pointerover', state.secondaryHandler);
    if (state.secondaryFocusHandler) root.removeEventListener('focusin', state.secondaryFocusHandler);
    if (state.drawer && state.drawer.parentNode) state.drawer.parentNode.removeChild(state.drawer);
    if (state.backdrop && state.backdrop.parentNode) state.backdrop.parentNode.removeChild(state.backdrop);
    if (state.activeCard) document.documentElement.classList.remove('sh-uc-lock');
    root.removeAttribute('data-sh-universal-ready');
    rootStates.delete(root);
  }

  function destroyWithin(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches(ROOT_SELECTOR)) destroyRoot(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(ROOT_SELECTOR).forEach(destroyRoot);
  }

  function setText(root, selector, value) {
    var node = root.querySelector(selector);
    if (node) node.textContent = value || '';
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>'"]/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character];
    });
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/`/g, '&#96;');
  }

  /* Collection return-position memory.
     Save the exact card + viewport offset before entering a PDP, then restore
     that position only on a genuine browser Back/Forward navigation. */
  var RETURN_STATE_KEY = 'sh:collection-return:v1';

  function collectionLocationKey() {
    return window.location.pathname + window.location.search;
  }

  function isCollectionPage() {
    return window.location.pathname.indexOf('/collections/') === 0;
  }

  function saveCollectionReturnState(event) {
    if (!isCollectionPage() || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target.closest && event.target.closest('a[href*="/products/"]');
    if (!link) return;
    var card = link.closest('[data-sh-uc-card],[data-sh-card],[data-sh-furniture-card]');
    if (!card) return;
    var handle = card.dataset.productHandle || card.dataset.handle || '';
    if (!handle) {
      var match = String(link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      handle = match ? match[1] : '';
    }
    if (!handle) return;
    try {
      sessionStorage.setItem(RETURN_STATE_KEY, JSON.stringify({
        location: collectionLocationKey(),
        handle: handle,
        group: card.dataset.group || '',
        top: Math.round(card.getBoundingClientRect().top),
        scrollY: Math.round(window.scrollY || window.pageYOffset || 0),
        savedAt: Date.now()
      }));
    } catch (error) {}
  }

  function cameFromHistoryTraversal(event) {
    if (event && event.persisted) return true;
    try {
      var entry = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (entry && entry.type === 'back_forward') return true;
      if (performance.navigation && performance.navigation.type === 2) return true;
    } catch (error) {}
    return false;
  }

  function restoreCollectionReturnState(event) {
    if (!isCollectionPage() || !cameFromHistoryTraversal(event)) return;
    var state;
    try { state = JSON.parse(sessionStorage.getItem(RETURN_STATE_KEY) || 'null'); } catch (error) { state = null; }
    if (!state || state.location !== collectionLocationKey() || !state.handle || Date.now() - Number(state.savedAt || 0) > 30 * 60 * 1000) return;

    var attempts = 0;
    function restore() {
      attempts += 1;
      var safeHandle = window.CSS && CSS.escape ? CSS.escape(state.handle) : String(state.handle).replace(/["\\]/g, '\\$&');
      var card = document.querySelector('[data-product-handle="' + safeHandle + '"],[data-handle="' + safeHandle + '"]');
      if (!card && state.group) {
        var safeGroup = window.CSS && CSS.escape ? CSS.escape(state.group) : String(state.group).replace(/["\\]/g, '\\$&');
        card = document.querySelector('[data-group="' + safeGroup + '"]');
      }
      if (card) {
        var currentTop = card.getBoundingClientRect().top;
        window.scrollTo(0, Math.max(0, (window.scrollY || window.pageYOffset || 0) + currentTop - Number(state.top || 0)));
        try { sessionStorage.removeItem(RETURN_STATE_KEY); } catch (error) {}
        return;
      }
      if (attempts < 14) window.setTimeout(restore, attempts < 5 ? 70 : 140);
      else if (Number.isFinite(Number(state.scrollY))) window.scrollTo(0, Number(state.scrollY));
    }
    window.setTimeout(restore, 0);
  }

  document.addEventListener('click', saveCollectionReturnState, true);
  window.addEventListener('pageshow', restoreCollectionReturnState);

  var api = { init: init };
  window.SickHausUniversalProductCards = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { destroyWithin(event.target); });

}());


/* SickHaus v37 — mobile native second-image swipe */
(function () {
  'use strict';

  var MEDIA_SELECTOR = [
    '.sh-uc-card__media',
    '.sh-lec__media',
    '.sh-te__media',
    '.sh-ha__media',
    '.sh-tx__media',
    '.sh-furn__media',
    '.sh-pi__media'
  ].join(',');
  var mobileQuery = window.matchMedia('(max-width: 749px)');
  var imageObserver = null;
  var mutationObserver = null;

  function sourceOf(image) {
    if (!image) return '';
    return image.currentSrc || image.getAttribute('src') || image.dataset.src || '';
  }

  function hydrate(image) {
    if (!image || image.dataset.shMobileHydrated === 'true') return;
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    if (image.dataset.sizes) image.sizes = image.dataset.sizes;
    if (image.dataset.src) image.src = image.dataset.src;
    image.loading = 'lazy';
    image.dataset.shMobileHydrated = 'true';
  }

  function pagination(media, count) {
    var node = media.querySelector(':scope > .sh-mobile-swipe-pagination');
    if (!node) {
      node = document.createElement('span');
      node.className = 'sh-mobile-swipe-pagination';
      node.setAttribute('aria-hidden', 'true');
      media.appendChild(node);
    }
    node.innerHTML = Array.from({ length: count }, function (_, index) { return '<i' + (index === 0 ? ' class="is-active"' : '') + '></i>'; }).join('');
    return node;
  }

  function updatePagination(track, dots) {
    if (!track || !dots) return;
    var width = track.clientWidth || 1;
    var items = dots.querySelectorAll('i');
    var max = Math.max(0, items.length - 1);
    var active = Math.max(0, Math.min(max, Math.round(track.scrollLeft / width)));
    items.forEach(function (item, index) { item.classList.toggle('is-active', index === active); });
  }

  function prepare(media) {
    if (!media || media.dataset.shMobileSwipeReady === 'true') return;
    var track = media.querySelector(':scope > a') || media.querySelector('a');
    if (!track) return;
    var images = track.querySelectorAll(':scope > img');
    if (images.length < 2) return;

    var primary = images[0];
    var usable = Array.prototype.filter.call(images, function (image, index) {
      if (index === 0) return true;
      var candidate = sourceOf(image) || image.dataset.src || image.dataset.srcset || '';
      return Boolean(candidate) && candidate !== sourceOf(primary);
    });
    if (usable.length < 2) return;

    media.dataset.shMobileSwipeReady = 'true';
    media.classList.add('sh-mobile-native-swipe');
    track.classList.add('sh-mobile-swipe-track');
    var dots = pagination(media, usable.length);

    var scrollFrame = 0;
    track.addEventListener('scroll', function () {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(function () {
        scrollFrame = 0;
        updatePagination(track, dots);
      });
    }, { passive: true });

    var startX = 0;
    var startY = 0;
    var suppressClick = false;
    var suppressTimer = 0;
    track.addEventListener('pointerdown', function (event) {
      startX = event.clientX;
      startY = event.clientY;
      suppressClick = false;
      window.clearTimeout(suppressTimer);
    }, { passive: true });
    track.addEventListener('pointermove', function (event) {
      var x = Math.abs(event.clientX - startX);
      var y = Math.abs(event.clientY - startY);
      if (x > 9 && x > y) suppressClick = true;
    }, { passive: true });
    track.addEventListener('pointerup', function () {
      if (!suppressClick) return;
      suppressTimer = window.setTimeout(function () { suppressClick = false; }, 350);
    }, { passive: true });
    track.addEventListener('click', function (event) {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    if (imageObserver) imageObserver.observe(media);
    else Array.prototype.slice.call(images, 1).forEach(hydrate);
    updatePagination(track, dots);
  }

  function scan(scope) {
    if (!mobileQuery.matches) return;
    var root = scope && scope.querySelectorAll ? scope : document;
    if (root.matches && root.matches(MEDIA_SELECTOR)) prepare(root);
    root.querySelectorAll(MEDIA_SELECTOR).forEach(prepare);
  }

  function start() {
    if ('IntersectionObserver' in window) {
      imageObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var media = entry.target;
          var track = media.querySelector(':scope > a') || media.querySelector('a');
          var images = track ? track.querySelectorAll(':scope > img') : [];
          Array.prototype.slice.call(images, 1).forEach(hydrate);
          imageObserver.unobserve(media);
        });
      }, { rootMargin: '450px 0px' });
    }

    scan(document);
    mutationObserver = new MutationObserver(function (mutations) {
      if (!mobileQuery.matches) return;
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) scan(node);
        });
      });
    });
    mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  document.addEventListener('shopify:section:load', function (event) { scan(event.target); });
  if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', function (event) { if (event.matches) scan(document); });
}());
